import React, { useMemo, useState } from 'react';
import { ShoppingBagIcon, XIcon, LeafIcon } from 'lucide-react';
import { TrustBadges } from '../components/TrustBadges';
import { Reveal } from '../components/Reveal';
import { products, skinTypes, brands, type SkinType } from '../data/products';
import { founder, salon } from '../data/content';
import { useStore } from '../contexts/StoreContext';
const brandNames = ['Alle merken', ...brands.map(b => b.name)] as const;
export function Shop() {
  const {
    add
  } = useStore();
  const [skin, setSkin] = useState<SkinType | null>(null);
  const [brand, setBrand] = useState<string>('Alle merken');
  const visible = useMemo(() => products.filter(p => (brand === 'Alle merken' || p.brand === brand) && (!skin || p.skinTypes.includes(skin))), [skin, brand]);
  const hasFilters = skin !== null || brand !== 'Alle merken';
  const clear = () => {
    setSkin(null);
    setBrand('Alle merken');
  };
  return <>
      {/* Hero */}
      <section className="bg-sand" data-id="element-523" data-mp="3dj8oeszp9hug">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-14 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-16 lg:pt-14" data-id="element-524" data-mp="3dk1ppefw3f57">
          <div className="lg:col-span-7" data-id="element-525" data-mp="3nad2zrlgr0or">
            <p className="rule-label text-[11px] uppercase tracking-widest text-clay" data-id="element-526" data-mp="3nbed9tunnj5f">
              Webshop · 100% groene vegan producten
            </p>
            <h1 className="mt-7 max-w-2xl font-display text-[2.7rem] font-light leading-[1.02] text-ink sm:text-6xl" data-id="element-527" data-mp="2ulpc9ghy3j3t">
              Plantbased, maar dan
              <span className="italic text-clay" data-id="element-528" data-mp="2zl28grupi2j2"> wél werkzaam.</span>
            </h1>
            <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-529" data-mp="33lcg08y5d101">
              Duurzaam is als een product zin heeft en doet waarvoor het gemaakt is. Daarom werk ik
              met cosmeceutical formules — bijna medicinale ingrediënten — in plaats van met kokos-
              en amandelolie die vaak niets doen.
            </p>
          </div>
          <div className="lg:col-span-5" data-id="element-530" data-mp="2jxunw4aqenc5">
            <img src={founder.withProducts} alt={`${founder.name} met de producten uit de webshop`} className="arch aspect-[4/5] w-full object-cover" data-id="element-531" data-mp="2jwtdm21ji4vh" />
          </div>
        </div>
      </section>

      <TrustBadges tone="shell" data-id="element-532" data-mp="2oxgz68701tib" />

      {/* Collection */}
      <section className="bg-sand py-14 lg:py-20" data-id="element-533" data-mp="2oy0hvr4h4lxe">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-534" data-mp="2173ifdtv7h31">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-535" data-mp="215jwp7pfwdtv">
            <aside className="lg:col-span-3" aria-label="Filters" data-id="element-536" data-mp="1w9uuewm1citv">
              <div className="lg:sticky lg:top-32" data-id="element-537" data-mp="2axy52jb0wip0">
                <div className="flex items-center justify-between" data-id="element-538" data-mp="2ayr5l2je2byp">
                  <h2 className="text-[11px] uppercase tracking-widest text-muted" data-id="element-539" data-mp="265ofj4dtlkn9">Huidtype</h2>
                  {hasFilters && <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-[12px] text-clay transition-colors duration-150 ease-soft hover:text-ink" data-id="element-540" data-mp="1if2a7kv7aegy">
                      <XIcon className="h-3 w-3" strokeWidth={1.5} data-id="element-541" data-mp="3tww1t50p3ife" />
                      Wissen
                    </button>}
                </div>
                <ul className="mt-4 border-t border-line" data-id="element-542" data-mp="3k61b9ogf1wo5">
                  {skinTypes.map(s => {
                  const isActive = skin === s;
                  return <li key={s} className="border-b border-line" data-id="element-543" data-mp="gplvhs9pcy58">
                        <button type="button" aria-pressed={isActive} onClick={() => setSkin(isActive ? null : s)} className={`flex w-full items-center justify-between py-3 text-left text-[14px] transition-colors duration-150 ease-soft ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-544" data-mp="gosy3hwmvkzr">
                          {s}
                          <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-150 ease-soft ${isActive ? 'bg-clay' : 'bg-transparent'}`} data-id="element-545" data-mp="37quypyidg4al" />
                        </button>
                      </li>;
                })}
                </ul>

                <h2 className="mt-9 text-[11px] uppercase tracking-widest text-muted" data-id="element-546" data-mp="3qmur3af9h9up">Merk</h2>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-547" data-mp="3lpcg7ehookk2">
                  {brandNames.map(b => <button key={b} type="button" aria-pressed={brand === b} onClick={() => setBrand(b)} className={`rounded-full border px-4 py-1.5 text-[12px] transition-colors duration-150 ease-soft ${brand === b ? 'border-ink bg-ink text-shell' : 'border-line text-muted hover:border-ink hover:text-ink'}`} data-id="element-548" data-mp="3hl4s9jj1v8mw">
                      {b}
                    </button>)}
                </div>

                <div className="mt-9 border-t border-line pt-6" data-id="element-549" data-mp="uhtreppvm197">
                  <h3 className="font-display text-xl text-ink" data-id="element-550" data-mp="uk6gdmu1xbvr">Weet je het niet zeker?</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-551" data-mp="zgwqzun37f9s">
                    Boek een gratis Shelfie Time: laat zien wat er in je kastje staat en ik zeg
                    eerlijk wat weg mag.
                  </p>
                  <a href={salon.whatsapp} className="mt-4 inline-block text-sm text-ink underline-offset-4 hover:underline" data-id="element-552" data-mp="1dgo31pu1mll5">
                    Advies via WhatsApp
                  </a>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9" data-id="element-553" data-mp="mdartn6qi5ea">
              <p className="text-[13px] text-muted" data-id="element-554" data-mp="me2nsz48u553">
                {visible.length} {visible.length === 1 ? 'product' : 'producten'}
                {skin ? ` voor een ${skin.toLowerCase()}` : ''}
                {brand !== 'Alle merken' ? ` van ${brand}` : ''}
              </p>

              {visible.length === 0 ? <div className="mt-8 border border-dashed border-line bg-shell px-8 py-16 text-center" data-id="element-555" data-mp="rf9ueuungya6">
                  <h3 className="font-display text-2xl text-ink" data-id="element-556" data-mp="10dljutjs5q11">Geen match binnen deze filters</h3>
                  <p className="mx-auto mt-3 max-w-sm text-[14px] leading-relaxed text-muted" data-id="element-557" data-mp="1f2hyxak983b5">
                    Voor deze combinatie hebben we niets in de collectie. Wis een filter of vraag
                    persoonlijk advies.
                  </p>
                  <button type="button" onClick={clear} className="mt-6 rounded-full border border-ink px-6 py-3 text-sm text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-shell" data-id="element-558" data-mp="1o3plwwhblhpx">
                    Filters wissen
                  </button>
                </div> : <ul className="mt-6 grid gap-x-6 gap-y-12 sm:grid-cols-2 xl:grid-cols-3" data-id="element-559" data-mp="1to5c2bs1i7ki">
                  {visible.map((p, i) => <li key={p.id} className="h-full" data-id="element-560" data-mp="1xsv6rxaypg3u" data-mpi={i}>
                      <Reveal delay={Math.min(i, 5) * 0.03} className="h-full" data-id="element-561" data-mp="1jycb04pixaui">
                        <article className="group flex h-full flex-col" data-id="element-562" data-mp="1oqlxfarfjss1">
                          <div className="relative overflow-hidden bg-shell" data-id="element-563" data-mp="1orx2rf0ix8l9">
                            <img src={p.image} alt={`${p.brand} ${p.name}`} className="aspect-[4/5] w-full object-cover transition-transform duration-300 ease-soft group-hover:scale-[1.02]" data-id="element-564" data-mp="1a30tjemqdpj2" />
                            {p.salonFavourite && <span className="absolute left-0 top-4 bg-ink px-3 py-1 text-[11px] tracking-wide text-shell" data-id="element-565" data-mp="1lgknhidtlo8e">
                                Salonfavoriet
                              </span>}
                          </div>
                          <p className="mt-4 text-[11px] uppercase tracking-widest text-clay" data-id="element-566" data-mp="17i4ibvvlw5sd">
                            {p.brand}
                          </p>
                          <h3 className="mt-1.5 font-display text-lg leading-snug text-ink" data-id="element-567" data-mp="12k2p4i4guq36">
                            {p.name}
                          </h3>
                          <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-568" data-mp="ss6r172dqzyy">{p.note}</p>
                          <div className="mt-auto flex items-center justify-between gap-3 border-t border-line pt-4" data-id="element-569" data-mp="nun0p8seqwqn">
                            <span className="font-display text-lg text-ink" data-id="element-570" data-mp="jqyscnvybx4c">€ {p.price}</span>
                            <button type="button" onClick={() => add(p)} aria-label={`${p.name} in winkelmand`} className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-[13px] text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-shell" data-id="element-571" data-mp="eu8fe9fg1hn5">
                              <ShoppingBagIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-572" data-mp="34dxquhswzb2i" />
                              In mand
                            </button>
                          </div>
                        </article>
                      </Reveal>
                    </li>)}
                </ul>}
            </div>
          </div>
        </div>
      </section>

      {/* Brand story */}
      <section className="border-t border-line bg-shell py-20 lg:py-24" data-id="element-573" data-mp="1o2bp3ok1pdr1">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-574" data-mp="1o07j912l64o4">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-575" data-mp="1sztvp8albgvv">
            <div className="lg:col-span-4" data-id="element-576" data-mp="2vx8xniog0cz9">
              <p className="text-[11px] uppercase tracking-widest text-clay" data-id="element-577" data-mp="2vwfx4zg2ujpk">De merken</p>
              <h2 className="mt-4 font-display text-4xl font-light leading-[1.08] text-ink" data-id="element-578" data-mp="2zwq1k7o8122b">
                Waarom juist deze vier
              </h2>
              <p className="mt-5 flex items-start gap-3 text-[15px] leading-relaxed text-muted" data-id="element-579" data-mp="39w8isgcqy9qo">
                <LeafIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} data-id="element-580" data-mp="2c903j33c9wx4" />
                emerginC plant een boom voor elk verkocht product en ontwikkelde de stabiele
                stay-C formule, astaxanthine en wortelzaadolie.
              </p>
            </div>
            <ul className="grid gap-8 lg:col-span-8 sm:grid-cols-2" data-id="element-581" data-mp="2m533c2apxjgw">
              {brands.map(b => <li key={b.name} className="border-t border-ink/70 pt-5" data-id="element-582" data-mp="1ti8obkwo7cat">
                  <h3 className="font-display text-xl text-ink" data-id="element-583" data-mp="1thyybxbml554">{b.name}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-584" data-mp="1yg0of27gy4q3">{b.description}</p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>
    </>;
}